import {
  ChatBubble,
  ChatBubbleOutlineOutlined,
  WorkspacePremiumOutlined,
} from '@mui/icons-material';
import {
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  Rating,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { Dispatch, SetStateAction, useState } from 'react';
import { useKaziNavigate } from '../../../../../Hooks/useKaziNavigate';
import { useMobileView } from '../../../../../Hooks/useMobileView';
import { useUser } from '../../../../../Hooks/useUser';
import { useGetApplicationsQuery } from '../../../../../Redux/api/applicationApi';
import { useAppSelector } from '../../../../../Redux/store';
import { KaziCard } from '../../../../../Shared Components/Kazi/Cards/KaziCard';
import { KaziAvatar } from '../../../../../Shared Components/Kazi/KaziAvatar';
import { KaziBookmark } from '../../../../../Shared Components/Kazi/KaziBookmark';
import { ProfileType } from '../../../../../Types/General';
import { Talent } from '../../../../../Types/Talent';
import {
  formatUserName,
  pluralize,
  removeUnwantedCharacters,
  toPascalCase,
} from '../../../../../Utils/String';
import { calculateAverageScoreAndReviews } from '../../../../../Utils/TalentUtils';
import { ViewTalentRatingModal } from '../../../../ActiveJobs/Components/ViewTalentRatingModal';
import { EducationSection } from '../Edit/Education/EducationSection';
import { ExperienceSection } from '../Edit/Experience/ExperienceSection';
import { PortfolioSection } from '../PortfolioSection';
import { TalentProfileButton } from '../TalentProfileButton';
import UpgradeSnackbar from './UpgradeSnackbar';

interface IViewTalentProfileProps {
  talentData: Talent;
  mode: 'view' | 'edit';
  setMode: Dispatch<SetStateAction<'view' | 'edit'>>;
}
export const ViewTalentProfile = ({
  talentData,
  mode,
  setMode,
}: IViewTalentProfileProps) => {
  /* ******************** Hooks ******************** */
  const { navigateToMessages } = useKaziNavigate();
  const userType = useAppSelector(state => state.Global.userType);
  const userId = useAppSelector(state => state.Global.userId);
  const { type, hasPremium } = useUser();
  const {
    data: apps,
    isLoading: appsLoading,
    isFetching: appsFetching,
  } = useGetApplicationsQuery(
    (type === ProfileType.Client && {
      client_locator: userId!,
      talent_locator: talentData.locator,
    }) ||
      skipToken,
  );
  const { flexDirection, isMobileView } = useMobileView();
  /* ******************** Variables ******************** */
  const [showUpgradeSnackbar, setShowUpgradeSnackbar] = useState(false);
  const [openViewTalentRatingModal, setOpenViewTalentRatingModal] =
    useState(false);

  const { averageScore, noOfRatings } =
    calculateAverageScoreAndReviews(talentData);
  /* ******************** Functions ******************** */
  const handleMessageClicked = () => {
    if (hasPremium) {
      const name = formatUserName(
        talentData?.first_name,
        talentData?.last_name,
        true,
      );
      navigateToMessages(talentData!.locator, name);
    } else {
      setShowUpgradeSnackbar(true);
    }
  };
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  return (
    <>
      <Grid
        container
        display={'flex'}
        flexDirection={'row'}
        sx={{ color: 'primary.main' }}
        columnGap={15}
      >
        <Grid item flex={1} display={'flex'} flexDirection={'column'} gap={4}>
          <Grid item display={'flex'} flexDirection={'row'} gap={4}>
            <Grid
              display={'flex'}
              flexDirection="column"
              justifyContent={'center'}
              gap={4}
            >
              <KaziAvatar
                locator={talentData?.locator ?? ''}
                first_name={talentData?.first_name ?? 'N/A'}
                last_name={talentData?.last_name}
                sx={{ alignSelf: 'center', fontSize: 50 }}
              />
              {isMobileView && (
                <TalentProfileButton
                  setMode={setMode}
                  profileLocator={talentData?.locator}
                />
              )}
            </Grid>
            <Grid item display={'flex'} flexDirection={'column'} gap={'16px'}>
              <Typography
                variant="lora-headline-small"
                fontWeight={'bold'}
              >{`${toPascalCase(talentData?.first_name ?? 'N/A')} ${
                talentData?.last_name.substring(0, 1).toUpperCase() ?? 'N/A'
              }.`}</Typography>
              <Grid item display={'flex'} flexDirection={'row'} gap={1}>
                <WorkspacePremiumOutlined />
                <Grid display={'flex'} flexDirection={'column'}>
                  <Typography variant="title-small">
                    {talentData?.title ?? 'N/A'}
                  </Typography>
                  {talentData?.resume?.expertise?.secondary_expertise && (
                    <Typography variant="title-small" color={'white.800'}>
                      {talentData.resume?.expertise.secondary_expertise}
                    </Typography>
                  )}
                  {talentData?.resume?.expertise?.tertiary_expertise && (
                    <Typography variant="title-small" color={'white.800'}>
                      {talentData.resume?.expertise.tertiary_expertise}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid item display={'flex'} flexDirection={'row'} gap={1}>
                <ChatBubbleOutlineOutlined />
                <Typography variant="title-small">
                  {talentData?.resume?.languages
                    .map(lang => lang.name)
                    .join(', ')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item display={'flex'} flexDirection={'column'}>
            <Typography
              variant="lora-title-medium"
              fontWeight={'bold'}
              marginBottom={'16px'}
            >
              About
            </Typography>
            <Grid
              display={'flex'}
              flexDirection={'column'}
              overflow={'auto'}
              maxHeight={'250px'}
            >
              <Typography variant="body-medium">
                {removeUnwantedCharacters(talentData?.resume?.summary ?? '')}
              </Typography>
            </Grid>
          </Grid>
          <Grid item display={'flex'} flexDirection={'column'}>
            <Typography
              variant="lora-title-medium"
              fontWeight={'bold'}
              marginBottom={'16px'}
            >
              Education
            </Typography>
            <EducationSection
              education={talentData?.resume?.education}
              readOnly
            />
          </Grid>
          <Grid item display={'flex'} flexDirection={'column'}>
            <Typography
              variant="lora-title-medium"
              fontWeight={'bold'}
              marginBottom={'16px'}
            >
              Experience
            </Typography>
            <ExperienceSection
              experience={talentData?.resume?.experience}
              readOnly
            />
          </Grid>
        </Grid>
        <Grid item flex={1} display={'flex'} flexDirection={'column'} gap={4}>
          <Grid
            item
            display={'flex'}
            flexDirection={'row'}
            marginBottom={14}
            justifyContent={'flex-end'}
            width={'50%'}
          >
            <Grid display={'flex'} flexDirection={'row'} columnGap={1}>
              <Grid>
                {userType === 'client' && (
                  <KaziBookmark
                    locator={talentData?.locator ?? ''}
                    sx={{ padding: '4px 8px' }}
                  />
                )}
              </Grid>
              {userType === 'client' && (
                <Grid>
                  <IconButton size="small" onClick={handleMessageClicked}>
                    {hasPremium ? (
                      <ChatBubbleOutlineOutlined
                        fontSize="large"
                        color="primary"
                        sx={{ padding: '2px 0' }}
                      />
                    ) : (
                      <ChatBubble
                        fontSize="large"
                        sx={{ padding: '2px 0', color: 'warning.main' }}
                      />
                    )}
                  </IconButton>
                </Grid>
              )}
              {!isMobileView && (
                <Grid>
                  <TalentProfileButton
                    setMode={setMode}
                    profileLocator={talentData?.locator}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          {talentData?.resume?.skills?.length > 0 && (
            <Grid item display={'flex'} flexDirection={'column'}>
              <Typography
                variant="lora-title-medium"
                fontWeight={'bold'}
                marginBottom={'16px'}
              >
                Skills
              </Typography>
              <Grid
                item
                display={'flex'}
                flexDirection={'row'}
                flexWrap={'wrap'}
                gap={1}
                width={'55%'}
              >
                {talentData?.resume?.skills?.map(
                  skill =>
                    typeof skill == 'string' && (
                      <Chip key={skill} label={skill} variant="outlined" />
                    ),
                )}
              </Grid>
            </Grid>
          )}
          {talentData?.resume?.certifications?.length > 0 &&
            !talentData?.resume?.certifications?.every(c => c.title === '') && (
              <Grid item display={'flex'} flexDirection={'column'}>
                <Typography
                  variant="lora-title-medium"
                  fontWeight={'bold'}
                  marginBottom={'16px'}
                >
                  Certifications
                </Typography>
                {talentData?.resume?.certifications.map((cert, idx) => (
                  <Grid
                    key={cert.title + idx}
                    item
                    display={'flex'}
                    flexDirection={'column'}
                    marginBottom={'16px'}
                  >
                    <Typography
                      key={cert.title}
                      variant="body-medium"
                      marginBottom={0.5}
                    >
                      {cert.title}
                    </Typography>
                    <Typography
                      key={`${cert.title} - ${cert.year}`}
                      color="black.200"
                      variant="body-medium"
                    >
                      Obtained {moment(cert.year).format('YYYY')}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
          {(talentData?.resume?.urls?.length ?? 0) > 0 && (
            <PortfolioSection urls={talentData.resume.urls!} />
          )}
          {(apps?.length ?? 0) > 0 && (
            <Grid
              item
              display={'flex'}
              flexDirection={'column'}
              sx={{ overflowY: 'scroll' }}
              minHeight={'10vh'}
            >
              <Typography variant="lora-title-medium" fontWeight={'bold'}>
                Applied Jobs
              </Typography>
              <Grid item display={'flex'} flexDirection={'column'}>
                {apps?.map((app, idx) => (
                  <>
                    {idx > 0 && <Divider />}
                    <Grid
                      key={`${app.job.title}-${idx}`}
                      display={'flex'}
                      flexDirection={'row'}
                      paddingY={'16px'}
                      gap={10}
                      alignItems={'center'}
                    >
                      <Grid display={'flex'} flexDirection={'column'}>
                        <Typography variant="body-medium">
                          {app.job.title}
                        </Typography>
                      </Grid>
                      <Grid display={'flex'} flexDirection={'column'} flex={1}>
                        <Link
                          //TODO: Replace with route url constant
                          href={`${window.location.origin}/job-postings/applications/${app.job.locator}?application=${app.locator}`}
                        >
                          <Typography
                            margin={0}
                            padding={0}
                            variant="body-medium"
                          >
                            View Application
                          </Typography>
                        </Link>
                      </Grid>
                    </Grid>
                  </>
                ))}
              </Grid>
            </Grid>
          )}
          {(type === ProfileType.Client || talentData.locator === userId) &&
            (talentData?.ratings?.history?.length ?? 0) > 0 &&
            averageScore > 0 && (
              <>
                <Grid item md={12} lg={7}>
                  <KaziCard
                    title="Reviews"
                    titleVariant={'lora-title-medium'}
                    cardAction={() => setOpenViewTalentRatingModal(true)}
                    list={
                      <Grid
                        item
                        display="flex"
                        flexDirection="row"
                        gap={3}
                        alignItems="center"
                      >
                        <>
                          <Rating
                            value={averageScore}
                            readOnly
                            precision={0.1}
                          />
                          <Typography variant="lora-title-medium">
                            {averageScore}
                          </Typography>
                          <Divider orientation="vertical" flexItem />
                          <Typography variant="lora-body-medium">
                            {pluralize(noOfRatings, 'rating', 'ratings')}
                          </Typography>
                        </>
                      </Grid>
                    }
                  />
                </Grid>
                <ViewTalentRatingModal
                  open={openViewTalentRatingModal}
                  setOpen={setOpenViewTalentRatingModal}
                  talentData={talentData}
                />
              </>
            )}
        </Grid>
      </Grid>
      <UpgradeSnackbar
        open={showUpgradeSnackbar}
        setOpen={setShowUpgradeSnackbar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        message={
          'Upgrade to Premium to message Talents you are not actively working with.'
        }
      />
    </>
  );
};
